<script lang="ts">
    import Table from '../../../components/Table.svelte';
    import Button from '../../../components/Button.svelte';
    import Dropdown from '../../../components/Dropdown.svelte';
    import DateRange from '../../../components/DateRange.svelte';
    import Pagination from '../../../components/Pagination.svelte';
    import SearchInput from '../../../components/SearchInput.svelte';
    import { type DonationRequest } from '../../../lib/api/employer/leaverequest';
    import employerLeaveRequestService from '../../../lib/api/employer/leaverequest';
    import { onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { getStageMeta, stageFromStatusName, STAGE_LABEL, type LifecycleStage, type LifecycleTone } from '../../../lib/donationLifecycle';

    // Pending Requests: only stages awaiting employer review (not paid/rejected/completed).
    const statusOptions: Array<{ uuid: string; name: string }> = (
      ['submitted', 'awaiting_employer_registration'] as const
    ).map((s) => ({ uuid: s, name: STAGE_LABEL[s] }));

    // Canonical lifecycle status badge as an HTML string (icon by tone, matches StageBadge.svelte).
    function stageBadgeIcon(tone: LifecycleTone): string {
      if (tone === 'red') {
        return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
      }
      if (tone === 'amber' || tone === 'grey') {
        return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';
      }
      return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>';
    }

    function renderStageBadge(row: { lifecycle_stage?: string; status?: string }): string {
      const stage = (row.lifecycle_stage as LifecycleStage) || stageFromStatusName(row.status);
      const meta = getStageMeta(stage);
      return `<span class="lc-badge ${meta.badgeClass}">${stageBadgeIcon(meta.tone)}<span>${meta.label}</span></span>`;
    }

    // User data - would come from store in real implementation
    let user = { uuid: 'user-123' };
    let loading = false;
    let error: string | null = null;
  
    // State for requests data
    let leaveRequests: DonationRequest[] = [];
    let filteredRequests: any[] = [];
    let currentPage = 1;
    let totalPages = 1;
    let totalItems = 0;

    // Filter states (canonical lifecycle stage; this page defaults to newly submitted requests)
    const DEFAULT_STAGE: LifecycleStage = 'submitted';
    let selectedStatus: string = DEFAULT_STAGE;
    let phrase = '';
    let startDate = '';
    let endDate = '';

    // Function to fetch leave requests
    async function fetchLeaveRequests(isSearch = false) {
      loading = true;
      error = null;
      
      try {
        const requestedPage = isSearch ? 1 : currentPage; // Reset to page 1 for new searches
        
        const filters = {
          page: requestedPage,
          per_page: 10,
          lifecycle_stage: selectedStatus || DEFAULT_STAGE, // Always filter by lifecycle stage
          phrase: phrase.trim() || undefined, // Ensure phrase is trimmed and only passed if not empty
          start_date: startDate || undefined,
          end_date: endDate || undefined
        };

        console.log('Fetching leave requests with filters:', filters);
        const response = await employerLeaveRequestService.getLeaveRequests(filters);
        console.log('API Response:', response);
        
        if (response && response.success) {
          // Extract data based on response structure
          let allRequests: DonationRequest[] = [];
          
          if (Array.isArray(response.data)) {
            allRequests = response.data;
          } else if (response.data && typeof response.data === 'object') {
            if (Array.isArray(response.data.data)) {
              allRequests = response.data.data;
            }
          }

          // No need to filter client-side since API already filters by status
          leaveRequests = allRequests;

          if (response.paging) {
            totalPages = response.paging.last_page || 1;
            totalItems = response.paging.total || leaveRequests.length; // Use server total
            currentPage = requestedPage;
          } else {
            totalPages = 1;
            totalItems = leaveRequests.length;
            currentPage = 1;
          }
          
          if (leaveRequests.length > 0) {
            filteredRequests = leaveRequests.map(request => ({
                uuid: request.uuid,
                id: request.uuid,
                employee: request.user?.full_name || 'Unknown',
                hours: `${request.leave_hours} hrs`,
                charity: request.charity?.name || 'N/A',
                date: new Date(request.created_at).toLocaleDateString('en-US', { 
                  day: '2-digit', 
                  month: 'short', 
                  year: 'numeric' 
                }),
                status: request.donation_request_status?.name ||
                       (request.donation_request_status_uuid === 'approved' ? 'Approved' :
                        request.donation_request_status_uuid === 'rejected' ? 'Rejected' : 'Pending'),
                lifecycle_stage: request.lifecycle_stage
            }));
          } else {
            filteredRequests = [];
            // Don't set error for empty results - it's a valid empty state
            error = null;
          }
        } else {
          leaveRequests = [];
          filteredRequests = [];
          error = null; // Don't show error for empty results
        }
      } catch (err) {
        console.error('Error fetching leave requests:', err);
        error = 'Failed to load pending requests. Please try again.';
        leaveRequests = [];
        filteredRequests = [];
      } finally {
        loading = false;
      }
    }
  
    // Debounce function
    function debounce(func: Function, wait: number) {
      let timeout: ReturnType<typeof setTimeout>;
      return function executedFunction(...args: any[]) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    // Handle search input
    function handleSearch(event: CustomEvent<string>) {
      console.log('handleSearch called');
      console.log('Event:', event);
      console.log('Event type:', event.type);
      console.log('Event detail:', event.detail);
      
      phrase = event.detail || '';
      console.log('Phrase updated to:', phrase);
      debouncedSearch();
    }

    // Create debounced search function with shorter timeout for testing
    const debouncedSearch = debounce(() => {
      console.log('debouncedSearch called');
      console.log('Current phrase:', phrase);
      fetchLeaveRequests(true);
    }, 1000);
  
    // Filter functions
    function handleStatusChange(value: string) {
      selectedStatus = value;
      fetchLeaveRequests(false);
    }
  
      function handleDateApply(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
      fetchLeaveRequests(false);
    }
  
    // Table columns
    const columns = [
      {
        key: 'employee',
        label: 'Employee',
        // No width - CSS will handle flexible sizing
      },
      {
        key: 'hours',
        label: 'Hours',
        width: '100px', // Fixed width - won't grow
      },
      {
        key: 'charity',
        label: 'Charity',
        // No width - CSS will handle flexible sizing (gets more space)
      },
      {
        key: 'date',
        label: 'Date',
        width: '160px', // Fixed width - won't grow (increased for date format)
      },
      {
        key: 'status',
        label: 'Status',
        width: '168px', // Fixed width - won't grow
        align: 'center',
        render: (_value: string, row: any) => renderStageBadge(row)
      }
    ];

    function handleRowClick(row: any) {
      // Navigate to view leave request page
      if (row && row.uuid) {
        push(`/view-leave-request?id=${row.uuid}`);
      }
    }

    // Pagination functions
    function goToPage(page: number) {
      if (page >= 1 && page <= totalPages) {
        currentPage = page;
        fetchLeaveRequests(false);
      }
    }
  
    onMount(async () => {
      // Check URL parameters for initial status filter, mapped to canonical lifecycle stages.
      const urlParams = new URLSearchParams(window.location.search);
      const statusParam = urlParams.get('status');

      if (statusParam === 'pending') {
        selectedStatus = 'submitted';
      } else if (statusParam === 'approved') {
        selectedStatus = 'approved';
      } else {
        // Default to newly submitted requests for this page
        selectedStatus = DEFAULT_STAGE;
      }

      // Fetch leave requests
      fetchLeaveRequests();
    });
  </script>
  
  <div class="pending-requests-page">
    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filters-left">
        <SearchInput
          placeholder="Search pending requests..."
          on:search={handleSearch}
          bind:value={phrase}
          width="250px"
        />
        
        <Dropdown
          options={statusOptions}
          bind:selectedValue={selectedStatus}
          placeholder="Status"
          onSelectionChange={handleStatusChange}
        />
        
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Date Range"
          on:apply={handleDateApply}
        />
      </div>
    </div>
  
    {#if loading}
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading pending requests...</p>
      </div>
    {:else if error}
      <div class="error-container">
        <p class="error-message">{error}</p>
        <Button variant="primary" on:click={() => fetchLeaveRequests()}>
          Try Again
        </Button>
      </div>
    {:else if filteredRequests.length === 0}
      <div class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <h3>No pending requests found</h3>
        <p>There are no donation requests awaiting your review.</p>
      </div>
    {:else}
      <div class="table-section pending-requests-table">
        <Table 
          columns={columns}
          data={filteredRequests}
          onRowClick={handleRowClick}
        />
      </div>
      
      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            on:pageChange={(event) => goToPage(event.detail.page)} 
          />
        </div>
      {/if}
    {/if}
  </div>
  
  <style>
    .pending-requests-page {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      padding: 1.5rem;
      background-color: #f7fafc;
      min-height: 100vh;
    }
  
    .filters-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 2rem;
      background: white;
      padding: 1.5rem;
      border-radius: 12px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      width: 100%;
    }
  
    .filters-left {
      display: flex;
      gap: 1rem;
      align-items: center;
      flex: 1;
    }
  
    .table-section {
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      overflow: hidden;
    }

    /* Pending Requests Table - Optimize column widths */
    /* Fixed columns won't grow */
    :global(.pending-requests-table .data-table th:nth-child(2)),
    :global(.pending-requests-table .data-table td:nth-child(2)) {
      /* Hours column */
      width: 100px !important;
      max-width: 100px !important;
      min-width: 100px !important;
    }

    :global(.pending-requests-table .data-table th:nth-child(4)),
    :global(.pending-requests-table .data-table td:nth-child(4)) {
      /* Date column */
      width: 160px !important;
      max-width: 160px !important;
      min-width: 160px !important;
    }

    :global(.pending-requests-table .data-table th:nth-child(5)),
    :global(.pending-requests-table .data-table td:nth-child(5)) {
      /* Status column */
      width: 140px !important;
      max-width: 140px !important;
      min-width: 140px !important;
    }

    /* Flexible columns - Charity gets more space */
    :global(.pending-requests-table .data-table th:nth-child(1)),
    :global(.pending-requests-table .data-table td:nth-child(1)) {
      /* Employee column - smaller share of flexible space */
      min-width: 150px;
      width: 25% !important;
    }

    :global(.pending-requests-table .data-table th:nth-child(3)),
    :global(.pending-requests-table .data-table td:nth-child(3)) {
      /* Charity column - larger share of flexible space */
      min-width: 250px;
      width: 35% !important;
    }
  
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  
    .loading-spinner {
      width: 2rem;
      height: 2rem;
      border: 3px solid #f3f4f6;
      border-top: 3px solid var(--color-primary);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 1rem;
    }
  
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  
    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  
    .error-message {
      color: #dc2626;
      font-size: 1.1rem;
      margin-bottom: 1rem;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .empty-icon {
      color: #9ca3af;
      margin-bottom: 1rem;
    }

    .empty-state h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0 0 0.5rem;
      color: #111827;
    }

    .empty-state p {
      color: #6b7280;
      margin-bottom: 1.5rem;
    }
  
    .pagination-container {
      display: flex;
      justify-content: center;
      margin-top: 1.5rem;
    }
  
    /* Action buttons in the table */
    :global(.action-buttons) {
      display: flex;
      gap: 0.5rem;
    }
  
    :global(.action-button) {
      padding: 0.75rem 1.5rem;
      border-radius: 50rem;
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
    }
  
    :global(.action-button.view) {
      background-color: #1f2937;
      color: white;
    }
  
    :global(.action-button.edit) {
      background-color: transparent;
      color: #1f2937;
      border: 1px solid #e5e7eb;
    }
  
    :global(.action-button.view:hover) {
      background-color: #111827;
    }
  
    :global(.action-button.edit:hover) {
      background-color: #f9fafb;
    }
  
    /* Responsive design */
    @media (max-width: 768px) {
      .filters-section {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
      }
  
      .filters-left {
        flex-direction: column;
        gap: 0.75rem;
      }
    }
  </style> 